import React, { useState, useEffect, useRef } from "react";
import { Skeleton } from "@mui/material";
import CurrencyConverter from "@heartinz/currency_subunit_converter";
import DateConverter from "@heartinz/date_and_time_processor";

import { useNetwork } from "../../../context/NetworkContext";
import { useShowAlert } from "../../../context/AlertContext";
import AxiosInstance from "../../../utils/AxiosInstance";
import handleError from "../../../constants/handleError";
import ReactPaginate from "react-paginate";
import { useAuth } from "../../../context/AuthProvider";
import { usePermission } from "../../../context/PermissionContext";
import Messages from "../../../constants/Messages";
import Table from "../../../constants/Table";
import { toSecondaryQuantity } from "@heartinz/finance";
import ProductSearchList from "../../../constants/ProductSearchList";
import { secondaryQuanity } from "../../../utils/ProductHandleChange";
import { formatCurrencyFun } from "../../../utils/formatCurrencyFunc";
import { RefreshIcon, SearchIcon } from "../../../constants/Icons";

const StockTrash = ({ isActive }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isStockData, setStockData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [isSearchTerm, setSearchTerm] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const [suggestionLoading, setSuggestionLoading] = useState(false);
  const [ProductSuggestions, setProductSuggestions] = useState([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const { checkNetwork } = useNetwork();
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);
  const ProductSuggestionsRef = useRef(null);
  const suggestionRefs = useRef([]);
  const showAlert = useShowAlert();
  const perPage = 10;
  const { isUser } = useAuth();
  const { permission, hasPermission } = usePermission();
  const debounceTimerRef = useRef(null);
  const controllerRef = useRef(null);


  const handleSearch = async (term) => {
    if (hasPermission("STOCK", "PRODUCT:search") || permission === undefined) {
      setSearchTerm(term);
      setHasSearched(true);

      if (!term.trim()) {
        setProductSuggestions([]);
        await handleTrashList(0, "");
        setCurrentPage(0);
        return;
      }
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      if (controllerRef.current) {
        controllerRef.current.abort();
      }
      debounceTimerRef.current = setTimeout(async () => {
        try {
          if (!checkNetwork()) return;
          setSuggestionLoading(true);
          controllerRef.current = new AbortController();
          const response = await AxiosInstance.get(
            `module/product/search-hybrid-active?searchtxt=${term}`,
            { signal: controllerRef.current.signal },
          );

          setProductSuggestions(response.data.results || []);
        } catch (error) {
          if (error.name !== "CanceledError" && error.name !== "AbortError") {
            handleError(error, showAlert);
          }
        } finally {
          setSuggestionLoading(false);
        }
      }, 200);
    } else {
      showAlert("warning", Messages.AUTH.PERMISSION_REQUIRED);
    }
  };

  const handleSearchFind = () => {
    const currentInputValue = searchInputRef.current?.value || "";
    setSearchTerm(currentInputValue);
    // setSelectedProductCode("");
    if (!currentInputValue.trim()) {
      handleTrashList(0, "");
    } else {
      handleTrashList(0, currentInputValue);
    }
    setProductSuggestions([]);
    setHasSearched(false);
    setCurrentPage(0);
  };

  const handleSelectProduct = (product) => {
    const code = product.code;
    // setSearchTerm(code);
    // setSelectedProductCode(code);
    setProductSuggestions([]);
    setHasSearched(false);
    if (searchInputRef.current) {
      searchInputRef.current.value = "";
    }
    handleTrashList(0, code);
    setCurrentPage(0);
  };

  const handleOutsideClick = (e) => {
    if (
      ProductSuggestionsRef.current &&
      !ProductSuggestionsRef.current.contains(e.target)
    ) {
      setProductSuggestions([]);
      setHasSearched(false);
      setSearchTerm("");
    }
  };

  const handleTrashList = async (page = 0, term = "") => {
    setIsLoading(true);
    try {
      if (!checkNetwork()) return;
      const response = await AxiosInstance.get(
        `module/stock/trash?pageno=${
          page + 1
        }&limit=${perPage}&searchtxt=${term}`,
      );
      const results = response.data.results || [];
      setStockData(results);
      if (results.length === 0) {
        setTotalPages(0);
        setTotalItems(0);
        setCurrentPage(0);
      } else {
        setTotalPages(response.data.totalPages || 0);
        setTotalItems(response.data.totalItems || 0);
      }
    } catch (error) {
      handleError(error, showAlert);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  //   const handleSearch = (e) => {
  //     const query = e.target.value.toLowerCase();
  //     setSearchQuery(query);
  //     const filtered = isStockData.filter(
  //       (item) =>
  //         item?.stockLinkInfo?.code?.toLowerCase().includes(query) ||
  //         item?.stockLinkInfo?.title?.toLowerCase().includes(query)
  //     );
  //     setFilteredData(filtered);
  //   };

  const getColor = (status) => {
    switch (status.toLowerCase()) {
      case "main":
        return "bg-label-orange";
      case "sub":
        return "bg-label-peach";
      case "hub":
        return "bg-label-night";
      default:
        return "bg-label-secondary";
    }
  };

  const handleSuggestionNavigation = (e) => {
    if (e.key === "Enter") {
      if (activeIndex >= 0 && ProductSuggestions[activeIndex]) {
        e.preventDefault();
        handleSelectProduct(ProductSuggestions[activeIndex]);
      } else {
        e.preventDefault();
        handleSearchFind();
      }
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((prev) =>
        prev < ProductSuggestions.length - 1 ? prev + 1 : 0,
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((prev) =>
        prev > 0 ? prev - 1 : ProductSuggestions.length - 1,
      );
    }
  };

  useEffect(() => {
    if (activeIndex >= 0 && suggestionRefs.current[activeIndex]) {
      suggestionRefs.current[activeIndex].scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [activeIndex]);

  useEffect(() => {
    if (!isActive) return;

    // small timeout ensures DOM is visible after tab switch
    const timer = setTimeout(() => {
      searchInputRef.current?.focus();
    }, 0);

    return () => clearTimeout(timer);
  }, [isActive]);

  useEffect(() => {
    document.addEventListener("click", handleOutsideClick);
    return () => {
      document.removeEventListener("click", handleOutsideClick);
    };
  }, []);

  useEffect(() => {
    handleTrashList(currentPage, isSearchTerm);
  }, [currentPage]);

  const handleRefresh = async () => {
    await handleTrashList(currentPage, isSearchTerm);
  };

  return (
    <>
      <div>
        <div className="page-wrapper">
          <div className="page-content">
            <div className="page-breadcrumb d-flex align-items-center mb-3 gap-2">
              <div className="pb-1">
                <button
                  type="button"
                  className="btn btn-sm btn-light p-1 shadow-none"
                  onClick={handleRefresh}
                  disabled={isLoading}
                  title="Refresh list"
                >
                  <RefreshIcon iClass={`${isLoading ? "bx-spin" : ""}`}/>
                </button>
              </div>
              <div className="breadcrumb-title pe-3">List Stock Trash</div>
            </div>

            <div className="row">
              <div className="col">
                <div className="card radius-10 mb-0">
                  <div className="card-body px-0">
                    <div className="row px-3">
                      <div className="col-lg-5 me-auto">
                        <div className="ms-auto position-relative">
                          <div
                            className="input-group"
                            ref={ProductSuggestionsRef}
                          >
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Search product by code, title, etc..."
                              ref={searchInputRef}
                              value={isSearchTerm}
                              onChange={(e) => {
                                handleSearch(e.target.value);
                                setActiveIndex(-1);
                              }}
                              onKeyDown={handleSuggestionNavigation}
                              autoComplete="off"
                              autoFocus
                            />
                            <button className="input-group-text">
                              <SearchIcon/>
                            </button>
                          </div>
                          {hasSearched &&
                            isSearchTerm &&
                            !suggestionLoading &&
                            ProductSuggestions.length === 0 && (
                              <ul className="suggestions-list">
                                <li className="suggestion-item text-muted">
                                  No products data was found
                                </li>
                              </ul>
                            )}

                          {ProductSuggestions.length > 0 && (
                            <ProductSearchList suggest={ProductSuggestions}
                            suggestionRefs={suggestionRefs}
                             handleSelect={handleSelectProduct} activeIndex={activeIndex}
                             />
                            
                          )}
                        </div>
                      </div>
                    </div>
                    <div ref={containerRef}>
                      <Table
                        loading={isLoading}
                        data={isStockData}
                        hoverRow={false}
                        columns={[
                          { label: "S.No", render: (_, index) => index + 1 },
                          {
                            label: "Trashed Date",
                            render: (item) => (
                              <span className="text-heading fw-medium text-truncate">
                                {
                                  DateConverter.ISOToDisplayFormat(
                                    DateConverter.convertToTimeZone(
                                      item.createdAt,
                                      isUser?.timezone,
                                    ),
                                  ).date
                                }
                              </span>
                            ),
                          },
                          {
                            label: "Trashed From",
                            tdWidth: "280",
                            render: (item) => (
                              <span className="text-heading fw-medium text-truncate">
                                {item.status === "ACTIVE" ? (
                                  <>
                                    <span
                                      className={`badge rounded-pill ${getColor(
                                        item.removedat?.stype,
                                      )}`}
                                    >
                                      {item.removedat?.stype}
                                    </span>{" "}
                                    - {item.removedat?.title}
                                    <div>
                                      <small>{item.removedat?.email}</small>
                                    </div>
                                  </>
                                ) : (
                                  <>
                                    <span
                                      className={`badge rounded-pill ${getColor(
                                        item.trashedByStore?.stype,
                                      )}`}
                                    >
                                      {item.trashedByStore?.stype}
                                    </span>{" "}
                                    - {item.trashedByStore?.title}
                                    <div>
                                      <small>
                                        {item.trashedByStore?.email}
                                      </small>
                                    </div>
                                  </>
                                )}
                              </span>
                            ),
                          },
                          {
                            label: "Trashed Item",
                            tdWidth: "550",
                            render: (item) =>
                              item?.stockLinkInfo ? (
                                <>
                                  <span className="badge rounded-pill bg-label-navy">
                                    {item.stockLinkInfo.code}
                                  </span>{" "}
                                  - &nbsp;
                                  <span className="badge rounded-pill bg-label-retro mb-1">
                                    {item.stockLinkInfo.code}
                                  </span>{" "}
                                  -
                                  <span className="text-heading fw-medium text-wrap">
                                    &nbsp; {item.stockLinkInfo.title}
                                  </span>
                                  <div className="text-truncate d-sm-block mb-1">
                                    <small>MRP : </small> &nbsp;
                                    <span className="badge bg-label-fall rounded-pill">
                                      {formatCurrencyFun(item?.stockLinkInfo?.mrp)}
                                    </span>
                                    &nbsp;
                                    <small>Sale Price : </small> &nbsp;
                                    <span className="badge bg-label-food rounded-pill">
                                      {formatCurrencyFun(item?.stockLinkInfo?.saleprice)}
                                    </span>
                                  </div>
                                </>
                              ) : (
                                "-"
                              ),
                          },
                          {
                            label: "Quantity",
                            render: (item) => (
                              <span className="badge bg-label-sky">
                                
                                {Number(item?.stockLinkInfo?.isSecondaryUnit
                                ? secondaryQuanity(item.quantity,item?.stockLinkInfo?.unitConfig)
                                : item?.quantity)}{" "}<span className="font-11 ms-1">
                                {
                                (() => {
                                  const unitCode = item?.stockLinkInfo?.isSecondaryUnit
                                    ? item?.stockLinkInfo?.unitConfig?.secondaryUnitCode
                                    : item?.stockLinkInfo?.unitConfig?.primaryUnitCode;

                                  return unitCode !== "unit" ? unitCode : "";
                                })()
                              }</span>
                              </span>
                            ),
                          },
                          {
                            label: "Remarks",
                            tdWidth: "320",
                            render: (item) => (
                              <span className="text-wrap">
                                <small>
                                  {item.remarks ? `${item.remarks}` : ""}
                                </small>
                              </span>
                            ),
                          },
                        ]}
                      />
                    </div>
                    {totalPages > 0 && (
                      <div className="d-flex justify-content-center mt-3">
                        <ReactPaginate
                          previousLabel={"← Previous"}
                          nextLabel={"Next →"}
                          breakLabel={"..."}
                          onPageChange={({ selected }) => {
                            setIsLoading(true);
                            setCurrentPage(selected);
                          }}
                          pageRangeDisplayed={5}
                          pageCount={totalPages}
                          containerClassName="pagination"
                          pageClassName="page-item"
                          pageLinkClassName="page-link"
                          previousClassName="page-item"
                          previousLinkClassName="page-link"
                          nextClassName="page-item"
                          nextLinkClassName="page-link"
                          breakClassName="page-item"
                          breakLinkClassName="page-link"
                          activeClassName="active"
                          forcePage={currentPage}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default StockTrash;
